import 'src/styles/ui/notice-card.css';

import { faArrowLeft, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BlendAttributeIcon from 'src/components/blend-check/BlendAttributeIcon';
import BlendComparisonCard from 'src/components/blend-check/BlendComparisonCard';
import Alert from 'src/components/ui/Alert';
import Button from 'src/components/ui/Button';
import StatusBadge from 'src/components/ui/StatusBadge';
import { BLEND_CHECK_MODE } from 'src/hooks/useBlendCheck';
import { getAttributeSummary } from 'src/utils/blendAttributes';

/* keep in step with the blend-review-exit animation in blend-check.css */
const EXIT_DURATION_MS = 180;

const BLENDED_BATCH_NOTE =
  'This Batch contains a blend of different Material Types, and Material Usage counts. ' +
  'Please verify this will match build specifications before loading.';

/**
 * Full-screen review, one decision per screen. Rendered in a portal so it is
 * never clipped by the page it was opened from.
 */
const BlendReviewScreen = ({ check }) => {
  const { differences, isAcknowledged, acknowledge, closeReview, isReviewOpen } = check;
  const [stepIndex, setStepIndex] = useState(0);
  // Unmounting on close would cut the exit animation off, so the overlay stays
  // mounted for its duration.
  const [isMounted, setMounted] = useState(false);
  const [isClosing, setClosing] = useState(false);

  useEffect(() => {
    if (isReviewOpen) {
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!isMounted) {
      return undefined;
    }
    setClosing(true);
    const timer = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isReviewOpen, isMounted]);

  // Open on the first item still needing attention.
  useEffect(() => {
    if (!isReviewOpen) {
      return;
    }
    const firstPending = differences.findIndex(attribute => !isAcknowledged(attribute.key));
    setStepIndex(firstPending === -1 ? 0 : firstPending);
    // Only when the screen opens - re-running on every acknowledgement would fight
    // the step the operator is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewOpen]);

  // The review is an overlay, not a route, so Back has to dismiss it rather than
  // navigate away from the action the operator is in the middle of.
  useEffect(() => {
    if (!isMounted) {
      return undefined;
    }
    let dismissedByBack = false;
    window.history.pushState({ blendReview: true }, '');
    const handlePopState = () => {
      dismissedByBack = true;
      closeReview();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (!dismissedByBack && window.history.state?.blendReview) {
        window.history.back();
      }
    };
  }, [isMounted, closeReview]);

  useEffect(() => {
    if (!isMounted) {
      return undefined;
    }
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isMounted]);

  if (!isMounted || !differences.length) {
    return null;
  }

  const isBlendedBatch = check.mode === BLEND_CHECK_MODE.BLENDED_BATCH;
  const currentIndex = Math.min(stepIndex, differences.length - 1);
  const attribute = differences[currentIndex];
  const acknowledged = isAcknowledged(attribute.key);
  const isLastStep = currentIndex === differences.length - 1;
  // Blended batch has one side, so the badge carries the count; a comparison
  // just says the two sides differ.
  const stepBadge = isBlendedBatch ? getAttributeSummary(attribute) : 'Differs';

  // Acknowledging jumps to whatever still needs attention, and closing once nothing
  // does is the point of the flow. `isAcknowledged` still reflects the state before
  // this call, so the current attribute is excluded by key.
  const handleAcknowledge = () => {
    acknowledge(attribute.key);
    const nextPending = differences.findIndex(
      item => item.key !== attribute.key && !isAcknowledged(item.key)
    );

    if (nextPending === -1) {
      closeReview();
      return;
    }
    setStepIndex(nextPending);
  };

  // "Next" means the next step, not the next *pending* step - otherwise re-opening
  // an already-acknowledged review closes on the first tap, since nothing is pending.
  const goToNextStep = () => {
    if (!isLastStep) {
      setStepIndex(currentIndex + 1);
      return;
    }
    // On the last step, anything still unacknowledged (reached via Back) wins over closing.
    const firstPending = differences.findIndex(item => !isAcknowledged(item.key));
    if (firstPending === -1) {
      closeReview();
      return;
    }
    setStepIndex(firstPending);
  };

  return createPortal(
    <div
      className={classNames('blend-review', { 'blend-review--closing': isClosing })}
      role='dialog'
      aria-modal='true'
      aria-label={attribute.label}
    >
      <div className='blend-review__nav'>
        <button
          type='button'
          className='blend-review__back'
          aria-label='Back'
          onClick={closeReview}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <span className='blend-review__title'>{attribute.label}</span>
        <span className='blend-review__step-count'>
          {currentIndex + 1} of {differences.length}
        </span>
      </div>

      <div className='blend-review__body'>
        <div className='blend-review__inner'>
          <div className='blend-steps'>
            {differences.map((item, index) => (
              <i
                key={item.key}
                className={classNames({
                  done: isAcknowledged(item.key),
                  current: !isAcknowledged(item.key) && index === currentIndex,
                })}
              />
            ))}
          </div>

          <div className='blend-attribute'>
            <span
              className={classNames(
                'notice-tile',
                acknowledged ? 'notice-tile--ok' : 'notice-tile--warn'
              )}
            >
              {acknowledged ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                <BlendAttributeIcon attributeKey={attribute.key} />
              )}
            </span>
            <div className='blend-attribute__main'>
              <div className='blend-attribute__title'>{attribute.label}</div>
            </div>
            <StatusBadge uppercase={false} variant={acknowledged ? 'success' : 'warning'}>
              {acknowledged ? 'Acknowledged' : stepBadge}
            </StatusBadge>
          </div>

          <BlendComparisonCard
            attribute={attribute}
            source={check.source}
            destination={check.destination}
            lotsByUri={check.lotsByUri}
            single={isBlendedBatch}
          />

          {isBlendedBatch && isLastStep && (
            <Alert name='qr-instructions' variant='info' className='text-left'>
              {/* Alert nests children inside .alert-custom__text, so the row has to
                  be laid out here rather than via className on the Alert itself. */}
              <div className='d-flex align-items-center'>
                <FontAwesomeIcon icon={faInfoCircle} className='font-size-22 mr15' />
                <div className='fs-mobile-14'>{BLENDED_BATCH_NOTE}</div>
              </div>
            </Alert>
          )}
        </div>
      </div>

      <div className='blend-review__foot'>
        <div className='blend-review__foot-inner'>
          {currentIndex > 0 && (
            <Button variant='ghost' onClick={() => setStepIndex(currentIndex - 1)}>
              Back
            </Button>
          )}
          {acknowledged ? (
            <Button fill variant='primary' onClick={goToNextStep}>
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          ) : (
            <Button fill variant='warning' onClick={handleAcknowledge}>
              Acknowledge
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

BlendReviewScreen.propTypes = {
  check: PropTypes.shape({
    mode: PropTypes.string.isRequired,
    differences: PropTypes.array.isRequired,
    isAcknowledged: PropTypes.func.isRequired,
    acknowledge: PropTypes.func.isRequired,
    closeReview: PropTypes.func.isRequired,
    isReviewOpen: PropTypes.bool.isRequired,
    source: PropTypes.object,
    destination: PropTypes.object,
    lotsByUri: PropTypes.object,
  }).isRequired,
};

export default BlendReviewScreen;
